'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useConfig } from '@/appconfig/ConfigContext';

import { getSmartID } from './smartId';

// SmartId service (handles caching, deduping)

type SmartIdStatus = 'idle' | 'loading' | 'ready' | 'failed';

type SmartIdContextType = {
  smartId: string | null;
  status: SmartIdStatus;
  ensureLoaded: () => Promise<string | null>;
};

const defaultSmartIdContext: SmartIdContextType = {
  smartId: null,
  status: 'idle',
  ensureLoaded: async () => null,
};

const SmartIdContext = createContext<SmartIdContextType>(defaultSmartIdContext);

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

// Provides smartId to the app, fetches once and caches for session
export const SmartIdProvider = ({ children }: { children: React.ReactNode }) => {
  const config = useConfig();
  const [smartId, setSmartId] = useState<string | null>(null);
  const [status, setStatus] = useState<SmartIdStatus>('idle');
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Loads smartId if needed, only once per session
  const ensureLoaded = useCallback(async () => {
    if (!config?.featureFlags?.smartId) {
      if (!isMountedRef.current) return null;
      setStatus('failed');
      setSmartId(null);
      return null;
    }

    if (!isMountedRef.current) return null;
    setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    const id = await getSmartID();

    if (!isMountedRef.current) return null;
    if (id) {
      setSmartId(id);
      setStatus('ready');
      return id;
    }

    setSmartId(null);
    setStatus('failed');
    return null;
  }, [config?.featureFlags?.smartId]);

  useEffect(() => {
    if (!config) return;

    if (!config?.featureFlags?.smartId) {
      setSmartId(null);
      setStatus('failed');
      return;
    }

    if (status !== 'idle') return;

    let timeoutId: number | null = null;
    let idleId: number | null = null;
    const w = window as IdleWindow;

    const run = () => {
      void ensureLoaded();
    };

    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(run, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(run, 1000);
    }

    return () => {
      if (idleId !== null && w.cancelIdleCallback) {
        w.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [config, config?.featureFlags?.smartId, ensureLoaded, status]);

  const value = useMemo(
    () => ({
      smartId,
      status,
      ensureLoaded,
    }),
    [smartId, status, ensureLoaded]
  );

  return <SmartIdContext.Provider value={value}>{children}</SmartIdContext.Provider>;
};

export const useSmartId = () => {
  return useContext(SmartIdContext);
};
